import { areaDims, imageSizeApiValue } from './lib/area-geometry';
import { pickOne } from './lib/random';
import { isChoice, isPhoto, state } from './state';
import type { AnswerPrompt, Area, ChoiceQuestion, ColorIndex, PhotoQuestion, Question, RandomMode, Target } from './types';
import { fetchRandomPortraitFile } from './ui/portrait-picker';

export type ImageSize = '0.5K' | '1K' | '2K' | '4K';
export type GenStatus = 'queued' | 'running' | 'done' | 'error';
export type UpscaleStatus = 'running' | 'done' | 'error';

export interface SelectionChip {
  label: string;
  promptLabel: string;
  colorIndex: ColorIndex;
}

export interface PhotoInput {
  name: string;
  dataUrl: string;
  displayDataUrl: string;
  questionTitle: string;
}

export interface Generation {
  id: string;
  status: GenStatus;
  dataUrl?: string;
  mimeType?: string;
  originalDataUrl?: string;
  originalMimeType?: string;
  upscaleStatus?: UpscaleStatus;
  upscaleError?: string;
  upscaleStartedAt?: number;
  upscaleDurationMs?: number;
  prompt: string;
  perAreaPrompts: string[];
  selections: SelectionChip[];
  photoInputs: PhotoInput[];
  startedAt: number;
  durationMs?: number;
  areaCount: number;
  areaIndex: number;
  attempts: number;
  errorLog: string[];
  finalError?: string;
}

export type BatchMode = 'off' | 'shuffle' | 'rotate';

export interface GenerationSettings {
  parallelCount: number;
  batchMode: BatchMode;
}

const MAX_ATTEMPTS = 4;

export const generationSettings: GenerationSettings = {
  parallelCount: 1,
  batchMode: 'off',
};

export function setBatchMode(mode: BatchMode): void {
  if (generationSettings.batchMode === mode) return;
  generationSettings.batchMode = mode;
  notify();
}

export const generations: Generation[] = [];

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeGenerations(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const l of listeners) l();
}

export function setParallelCount(n: number): void {
  const clamped = Math.max(1, Math.min(10, Math.round(n)));
  if (clamped === generationSettings.parallelCount) return;
  generationSettings.parallelCount = clamped;
  notify();
}

export function notifyGenerations(): void {
  notify();
}

export function clearGenerations(): void {
  if (generations.length === 0) return;
  generations.length = 0;
  notify();
}

const POLL_INTERVAL_MS = 2000;

export async function startUpscale(
  gen: Generation,
  onComplete?: (g: Generation) => void,
): Promise<void> {
  if (!gen.dataUrl || !gen.mimeType) return;
  if (gen.upscaleStatus === 'running' || gen.upscaleStatus === 'done') return;
  gen.upscaleStatus = 'running';
  gen.upscaleError = undefined;
  gen.upscaleStartedAt = Date.now();
  gen.upscaleDurationMs = undefined;
  notify();

  try {
    const inline = dataUrlToInline(gen.dataUrl);
    const submitRes = await fetch('/api/upscale/submit', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ imageBase64: inline.data, mimeType: inline.mimeType }),
    });
    if (!submitRes.ok) {
      const body = await safeJson(submitRes);
      throw new Error(
        (body?.error as string | undefined) ?? `Submit failed (${submitRes.status})`,
      );
    }
    const submitData = (await submitRes.json()) as {
      request_id: string;
      status_url: string;
      response_url: string;
    };

    const statusQuery =
      `status_url=${encodeURIComponent(submitData.status_url)}` +
      `&response_url=${encodeURIComponent(submitData.response_url)}`;

    while (true) {
      await sleep(POLL_INTERVAL_MS);
      const statusRes = await fetch(`/api/upscale/status?${statusQuery}`);
      const body = (await safeJson(statusRes)) as {
        status?: string;
        imageBase64?: string;
        mimeType?: string;
        error?: string;
      } | null;
      if (!statusRes.ok) {
        throw new Error(body?.error ?? `Status failed (${statusRes.status})`);
      }
      if (!body) throw new Error('Empty status response');
      if (body.status === 'IN_QUEUE' || body.status === 'IN_PROGRESS') continue;
      if (body.status === 'ERROR') throw new Error(body.error ?? 'Upscale failed');
      if (body.status === 'COMPLETED' && body.imageBase64 && body.mimeType) {
        gen.originalDataUrl = gen.dataUrl;
        gen.originalMimeType = gen.mimeType;
        gen.dataUrl = `data:${body.mimeType};base64,${body.imageBase64}`;
        gen.mimeType = body.mimeType;
        gen.upscaleStatus = 'done';
        gen.upscaleDurationMs = Date.now() - (gen.upscaleStartedAt ?? Date.now());
        notify();
        onComplete?.(gen);
        return;
      }
      throw new Error(`Unexpected status: ${body.status ?? 'unknown'}`);
    }
  } catch (err) {
    gen.upscaleStatus = 'error';
    gen.upscaleError = (err as Error).message || String(err);
    gen.upscaleDurationMs = Date.now() - (gen.upscaleStartedAt ?? Date.now());
    notify();
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function safeJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function dataUrlToInline(dataUrl: string): { mimeType: string; data: string } {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) throw new Error('Invalid data URL');
  return { mimeType: match[1], data: match[2] };
}

export async function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = dataUrl;
  });
  return img;
}

export async function fileToDisplayDataUrl(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read photo'));
    reader.readAsDataURL(file);
  });
}

export async function fileToGrayscaleDataUrl(file: File): Promise<string> {
  const displayUrl = await fileToDisplayDataUrl(file);
  const img = await loadImage(displayUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  ctx.filter = 'grayscale(100%)';
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
}

function cropCanvas(
  source: HTMLCanvasElement,
  x: number,
  y: number,
  w: number,
  h: number,
  grayscale: boolean,
): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  if (grayscale) ctx.filter = 'grayscale(100%)';
  ctx.drawImage(source, x, y, w, h, 0, 0, w, h);
  return c;
}

function canvasToInline(canvas: HTMLCanvasElement): { mimeType: string; data: string } {
  const dataUrl = canvas.toDataURL('image/png');
  return dataUrlToInline(dataUrl);
}

interface Emission {
  qid: string;
  target: string;
  text: string;
}

interface QuestionRandomState {
  mode: RandomMode;
  cycleStart: number;
}

interface IterationPlan {
  selectedGroups: Array<{ qid: string; group: AnswerPrompt }>;
  selections: SelectionChip[];
  photoInputs: PhotoInput[];
}

interface BatchSnapshot {
  iterationPlans: IterationPlan[];
  questionOrder: string[];
  targets: Target[];
  openingPrompt: string;
  closingPrompt: string;
  targetTemplate: string;
  randomStateByQid: Map<string, QuestionRandomState>;
}

function cloneGroup(g: AnswerPrompt): AnswerPrompt {
  return {
    id: g.id,
    alternatives: g.alternatives.map((a) => ({ ...a })),
  };
}

function choiceIdxForIter(
  q: ChoiceQuestion,
  iterIdx: number,
  mode: BatchMode,
  locked: boolean,
): number[] {
  if (locked || mode === 'off' || q.options.length === 0) {
    return [...q.selected];
  }
  if (mode === 'rotate') {
    const base = q.selected[0] ?? 0;
    const idx = ((base + iterIdx) % q.options.length + q.options.length) % q.options.length;
    return [idx];
  }
  // shuffle
  return [Math.floor(Math.random() * q.options.length)];
}

async function buildPhotoInputForFile(
  file: File,
  question: PhotoQuestion,
): Promise<PhotoInput> {
  const [dataUrl, displayDataUrl] = await Promise.all([
    fileToGrayscaleDataUrl(file),
    fileToDisplayDataUrl(file),
  ]);
  return { name: file.name, dataUrl, displayDataUrl, questionTitle: question.title };
}

async function photoFilesForIterations(
  q: PhotoQuestion,
  count: number,
  mode: BatchMode,
  locked: boolean,
): Promise<Array<File | null>> {
  if (locked || mode === 'off') {
    return Array(count).fill(q.file);
  }
  // shuffle and rotate both pull fresh random portraits per iteration (no
  // deterministic portrait sequence exists).
  const out: Array<File | null> = [];
  for (let i = 0; i < count; i++) {
    const file = await fetchRandomPortraitFile();
    out.push(file ?? q.file);
  }
  return out;
}

function selectionChipsFor(questions: Question[], idxByQid: Map<string, number[]>): SelectionChip[] {
  const out: SelectionChip[] = [];
  for (const q of questions) {
    if (!isChoice(q)) continue;
    const sel = idxByQid.get(q.id) ?? [];
    for (const idx of sel) {
      const opt = q.options[idx];
      if (!opt) continue;
      out.push({
        label: opt.label,
        promptLabel: q.promptLabel,
        colorIndex: q.colorIndex,
      });
    }
  }
  return out;
}

async function buildIterationPlans(count: number, mode: BatchMode): Promise<IterationPlan[]> {
  // Precompute photo files per iteration (async).
  const photoFilesByQid = new Map<string, Array<File | null>>();
  for (const q of state.questions) {
    if (!isPhoto(q)) continue;
    const locked = state.lockedQuestionIds.has(q.id);
    photoFilesByQid.set(q.id, await photoFilesForIterations(q, count, mode, locked));
  }

  const plans: IterationPlan[] = [];
  for (let i = 0; i < count; i++) {
    const idxByQid = new Map<string, number[]>();
    const selectedGroups: Array<{ qid: string; group: AnswerPrompt }> = [];
    const photoInputs: PhotoInput[] = [];

    for (const q of state.questions) {
      const locked = state.lockedQuestionIds.has(q.id);
      if (isChoice(q)) {
        const sel = choiceIdxForIter(q, i, mode, locked);
        idxByQid.set(q.id, sel);
        for (const idx of sel) {
          const opt = q.options[idx];
          if (!opt || !opt.injects) continue;
          for (const group of opt.prompts) {
            selectedGroups.push({ qid: q.id, group: cloneGroup(group) });
          }
        }
      } else if (isPhoto(q)) {
        const file = photoFilesByQid.get(q.id)?.[i] ?? null;
        if (file) {
          photoInputs.push(await buildPhotoInputForFile(file, q));
          for (const group of q.prompts) {
            selectedGroups.push({ qid: q.id, group: cloneGroup(group) });
          }
        }
      }
    }

    plans.push({
      selectedGroups,
      selections: selectionChipsFor(state.questions, idxByQid),
      photoInputs,
    });
  }

  return plans;
}

async function snapshotBatch(count: number, mode: BatchMode): Promise<BatchSnapshot> {
  const questionOrder: string[] = [];
  const randomStateByQid = new Map<string, QuestionRandomState>();
  for (const q of state.questions) {
    questionOrder.push(q.id);
    randomStateByQid.set(q.id, { mode: q.randomMode, cycleStart: q.cycleIndex });
  }

  const iterationPlans = await buildIterationPlans(count, mode);

  return {
    iterationPlans,
    questionOrder,
    targets: state.targets.map((t) => ({ ...t, priority: [...t.priority] })),
    openingPrompt: state.openingPrompt,
    closingPrompt: state.closingPrompt,
    targetTemplate: state.targetTemplate,
    randomStateByQid,
  };
}

function resolveEmissions(snap: BatchSnapshot, iterIdx: number): Emission[] {
  const out: Emission[] = [];
  const plan = snap.iterationPlans[iterIdx];
  if (!plan) return out;
  for (const { qid, group } of plan.selectedGroups) {
    const alts = group.alternatives;
    if (alts.length === 0) continue;
    const qState = snap.randomStateByQid.get(qid);
    let alt;
    if (qState && qState.mode === 'cycle') {
      const idx = ((qState.cycleStart + iterIdx) % alts.length + alts.length) % alts.length;
      alt = alts[idx];
    } else {
      alt = pickOne(alts);
    }
    if (!alt) continue;
    const target = alt.target.trim();
    const text = alt.text.trim();
    if (!target || !text) continue;
    out.push({ qid, target, text });
  }
  return out;
}

function composeAreaPrompt(
  area: Area,
  emissions: Emission[],
  snap: BatchSnapshot,
): string {
  const parts: string[] = [];
  if (snap.openingPrompt.trim()) parts.push(snap.openingPrompt.trim());

  for (const at of area.targets) {
    const t = snap.targets.find((x) => x.id === at.targetId);
    if (!t) continue;
    const targetName = t.name;

    const candidates = emissions.filter((e) => e.target === targetName);
    if (candidates.length === 0) continue;

    const priorityTail = snap.questionOrder.filter((id) => !t.priority.includes(id));
    const order = [...t.priority, ...priorityTail];
    let winnerQid: string | null = null;
    for (const qid of order) {
      if (candidates.some((c) => c.qid === qid)) {
        winnerQid = qid;
        break;
      }
    }
    if (!winnerQid) continue;

    const finalists = candidates.filter((c) => c.qid === winnerQid);
    const chosen = pickOne(finalists);
    if (!chosen) continue;

    const description = at.description.trim();
    if (description) {
      parts.push(
        snap.targetTemplate
          .replaceAll('[DESCRIPTION]', description)
          .replaceAll('[UPDATE]', chosen.text),
      );
    } else {
      parts.push(chosen.text);
    }
  }

  if (snap.closingPrompt.trim()) parts.push(snap.closingPrompt.trim());
  return parts.join('\n\n');
}

export async function startGenerationBatch(): Promise<void> {
  if (!state.baseImage) return;
  if (state.areas.length === 0) return;

  const count = generationSettings.parallelCount;
  const mode = generationSettings.batchMode;
  const areaCount = state.areas.length;

  // Snapshot areas + baseImage + per-iteration selection/photo plans.
  const snapAreas: Area[] = state.areas.map((a) => ({
    ...a,
    targets: a.targets.map((t) => ({ ...t })),
    extraImages: a.extraImages.map((ei) => ({ ...ei })),
  }));
  const snap = await snapshotBatch(count, mode);
  const baseDataUrl = state.baseImage.dataUrl;
  const baseW = state.baseImage.width;
  const baseH = state.baseImage.height;

  // Advance persistent cycle counters for cycle-mode questions so the next
  // batch picks up where this one leaves off.
  for (const q of state.questions) {
    if (q.randomMode === 'cycle') q.cycleIndex += count;
  }

  for (let i = 0; i < count; i++) {
    const plan = snap.iterationPlans[i];
    const gen: Generation = {
      id: crypto.randomUUID(),
      status: 'queued',
      prompt: '',
      perAreaPrompts: [],
      selections: plan.selections,
      photoInputs: plan.photoInputs,
      startedAt: Date.now(),
      areaCount,
      areaIndex: 0,
      attempts: 0,
      errorLog: [],
    };
    generations.unshift(gen);
    void runIteration(gen, snapAreas, snap, i, baseDataUrl, baseW, baseH);
  }
  notify();
}

async function runIteration(
  gen: Generation,
  areas: Area[],
  snap: BatchSnapshot,
  iterIdx: number,
  baseDataUrl: string,
  baseW: number,
  baseH: number,
): Promise<void> {
  gen.status = 'running';
  gen.startedAt = Date.now();
  notify();

  try {
    const baseImg = await loadImage(baseDataUrl);
    const work = document.createElement('canvas');
    work.width = baseW;
    work.height = baseH;
    const workCtx = work.getContext('2d');
    if (!workCtx) throw new Error('Canvas 2D unavailable');
    workCtx.drawImage(baseImg, 0, 0);

    gen.dataUrl = work.toDataURL('image/png');
    gen.mimeType = 'image/png';
    notify();

    const areaPrompts: string[] = [];
    const emissions = resolveEmissions(snap, iterIdx);
    const iterPhotoInputs = snap.iterationPlans[iterIdx]?.photoInputs ?? [];

    for (let i = 0; i < areas.length; i++) {
      const area = areas[i];
      gen.areaIndex = i;
      gen.attempts = 0;
      notify();

      const { w, h } = areaDims(area);
      const prompt = composeAreaPrompt(area, emissions, snap);
      areaPrompts.push(prompt);

      if (!prompt.trim()) {
        // Nothing to do for this area; skip edit.
        continue;
      }

      // Build images: crop from current work canvas, optional full base image,
      // extra images, optional photo.
      const cropCanvasEl = cropCanvas(work, area.x, area.y, w, h, true);
      const images = [canvasToInline(cropCanvasEl)];
      if (area.includeBase) {
        const baseCanvasEl = cropCanvas(work, 0, 0, baseW, baseH, true);
        images.push(canvasToInline(baseCanvasEl));
      }
      for (const ei of area.extraImages) {
        images.push(dataUrlToInline(ei.dataUrl));
      }
      if (area.includePhoto && iterPhotoInputs.length > 0) {
        images.push(dataUrlToInline(iterPhotoInputs[0].dataUrl));
      }

      const result = await callGenerate(
        gen,
        prompt,
        images,
        area.imageSize,
        area.aspectRatio,
      );

      const resultImg = await loadImage(
        `data:${result.mimeType};base64,${result.imageBase64}`,
      );
      workCtx.drawImage(resultImg, area.x, area.y, w, h);
      gen.dataUrl = work.toDataURL('image/png');
      gen.mimeType = 'image/png';
      notify();
    }

    gen.perAreaPrompts = areaPrompts;
    gen.prompt = areaPrompts
      .map((p, i) => `### Area ${i + 1}\n${p}`)
      .join('\n\n');
    gen.areaIndex = areas.length;
    gen.status = 'done';
    gen.durationMs = Date.now() - gen.startedAt;
    notify();
  } catch (err) {
    gen.status = 'error';
    gen.finalError = (err as Error).message || String(err);
    gen.errorLog.push(gen.finalError);
    gen.durationMs = Date.now() - gen.startedAt;
    notify();
  }
}

async function callGenerate(
  gen: Generation,
  prompt: string,
  images: Array<{ mimeType: string; data: string }>,
  imageSize: ImageSize,
  aspectRatio: string,
): Promise<{ imageBase64: string; mimeType: string }> {
  let lastErr = 'Unknown error';
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    gen.attempts = attempt;
    notify();
    try {
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          prompt,
          images,
          imageSize,
          aspectRatio,
        }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { error?: string };
          if (body?.error) detail += `: ${body.error}`;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const data = (await res.json()) as { imageBase64: string; mimeType: string };
      return data;
    } catch (err) {
      const msg = (err as Error).message || String(err);
      gen.errorLog.push(`Area ${gen.areaIndex + 1} attempt ${attempt}: ${msg}`);
      lastErr = msg;
      notify();
    }
  }
  throw new Error(`Area ${gen.areaIndex + 1} failed after ${MAX_ATTEMPTS} attempts: ${lastErr}`);
}

// Used in various UI helpers that don't need live reference images anymore.
export function imageSizeApi(size: ImageSize): string {
  return imageSizeApiValue(size);
}
